import {
	createFileRoute,
	useNavigate,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { getPublicErrorMessageByCode } from "@proxi/convex";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/login")({
	component: LoginPage,
	validateSearch: (search: Record<string, unknown>) => ({
		redirect: (search.redirect as string) || undefined,
	}),
});

function LoginPage() {
	const router = useRouter();
	const navigate = useNavigate();
	const { redirect } = useSearch({ from: "/login" });
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!email.trim() || !password.trim()) {
			toast.error("Please fill in all fields");
			return;
		}

		setIsLoading(true);

		try {
			const result = await authClient.signIn.email({
				email: email.trim(),
				password,
			});

			if (result.error) {
				toast.error("Login failed", {
					description:
						result.error.message === "Invalid email or password"
							? "The email or password you entered is incorrect."
							: "Unable to sign in. Please check your credentials.",
				});
				return;
			}

			toast.success("Welcome back!");
			await router.invalidate();
			navigate({ to: redirect || "/" });
		} catch {
			toast.error("Something went wrong", {
				description: getPublicErrorMessageByCode(null),
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-background">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="font-bold text-2xl">Proxi Admin</CardTitle>
					<CardDescription>
						Enter your credentials to access the admin dashboard
					</CardDescription>
				</CardHeader>
				<form onSubmit={handleSubmit}>
					<CardContent className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								autoComplete="email"
								disabled={isLoading}
								id="email"
								onChange={(e) => setEmail(e.target.value)}
								placeholder="admin@proxi.app"
								required
								type="email"
								value={email}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								autoComplete="current-password"
								disabled={isLoading}
								id="password"
								onChange={(e) => setPassword(e.target.value)}
								required
								type="password"
								value={password}
							/>
						</div>
					</CardContent>
					<CardFooter className="pt-2">
						<Button className="w-full" disabled={isLoading} type="submit">
							{isLoading ? "Signing in..." : "Sign In"}
						</Button>
					</CardFooter>
				</form>
			</Card>
		</div>
	);
}
